// ============================================================
// useNarrativeSync - Narrative entry queue + Firestore listener
// ============================================================
// Extracted from useFirestoreSync. Handles:
//   - Queued narrative writes with 300ms debounce flush
//   - Narrative formatting (info, attack-result, fallback) - narrativeDoc.ts
//   - Real-time Firestore narrative listener
//   - Timestamp monotonicity for ordering
// ============================================================

import { useState, useEffect, useRef } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { toMs } from '../utils/timestamps';
import type { NarrativeEntryDoc } from '../types';
import type * as mpModule from '../multiplayer';
import { narrativeDocFrom } from './narrativeDoc';
import type { NarrativeInput } from './narrativeDoc';
import { NO_VEIL, type Veil } from '../engine/roster/hidden';
import { keepUnchanged } from '../utils/keepUnchanged';

const NARRATIVE_FLUSH_DEBOUNCE_MS = 300;

interface UseNarrativeSyncParams {
  sessionId: string | null;
  user: { sessionName?: string; displayName?: string | null } | null;
  /** Multiplayer service (DI) */
  mp: typeof mpModule;
  /** Surface sync failures to the user (toast); console.error stays for diagnostics */
  onSyncError?: (message: string) => void;
  /**
   * THE LOG'S ONE BOUNDARY (plan map-display, G1): every entry is veiled as it
   * is queued, so a hidden figure's act names its alias in the shared log.
   * A ref: the writer runs in callbacks made once. Absent: nothing is veiled.
   */
  veil?: MutableRefObject<Veil>;
}

export interface UseNarrativeSyncReturn {
  syncNarrativeToFirestore: (entry: NarrativeInput) => void;
  flushNarrativeQueue: () => void;
  firestoreNarrative: NarrativeEntryDoc[];
  narrativeClearTime: number;
  setNarrativeClearTime: Dispatch<SetStateAction<number>>;
  narrativeQueueRef: MutableRefObject<NarrativeEntryDoc[]>;
  narrativeFlushTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
}

type Timer = ReturnType<typeof setTimeout>;

/** Who the shared log says wrote an entry. */
const userNameOf = (user: UseNarrativeSyncParams['user']): string =>
  user?.sessionName || user?.displayName || 'Unknown';

/** A strictly increasing timestamp, so entries written in one millisecond keep their order. */
function nextTimestamp(lastRef: MutableRefObject<number>): number {
  const ts = Math.max(Date.now(), lastRef.current + 1);
  lastRef.current = ts;
  return ts;
}

/**
 * A stored document as the log shows it: its time as a NUMBER, and
 * `structured` parsed back or left as the string it was.
 *
 * THE TIME IS NORMALISED HERE, ONCE (2026-10-06). Firestore hands a written
 * Date back as a Timestamp object while the type says `number`, and what
 * trusted the type was quietly wrong: the merge of the log with the vox
 * subtracted an object from a number, so every log line sorted ahead of
 * every vox line whatever the hour.
 */
function readNarrativeDoc(d: { id: string; data: NarrativeEntryDoc }): NarrativeEntryDoc {
  const docData: NarrativeEntryDoc = { id: d.id, ...d.data, timestamp: toMs(d.data.timestamp) };
  if (typeof docData.structured !== 'string') return docData;
  try {
    docData.structured = JSON.parse(docData.structured);
  } catch {
    /* leave as string */
  }
  return docData;
}

/**
 * The shared log, live and in timestamp order. A delivery that changes no line keeps the log it held, and an unchanged
 * line keeps its object (plan sync-listeners): every write anywhere re-delivers the whole log, and as new objects the
 * shell rendered from the root for each.
 */
function useNarrativeListener(sessionId: string | null, mp: typeof mpModule) {
  const [firestoreNarrative, setFirestoreNarrative] = useState<NarrativeEntryDoc[]>([]);
  // What is held, read without scheduling a render: handed an updater, React renders once even to keep the same.
  const held = useRef(firestoreNarrative);
  useEffect(() => {
    if (!sessionId) return;
    const unsub = mp.onNarrativeUpdate(sessionId, ({ entries }) => {
      const read = entries.map(readNarrativeDoc).sort((a, b) => toMs(a.timestamp) - toMs(b.timestamp));
      const next = keepUnchanged(held.current, read, (e) => String(e.id));
      if (next === held.current) return;
      held.current = next;
      setFirestoreNarrative(next);
    });
    return () => unsub();
  }, [sessionId, mp]);
  return firestoreNarrative;
}

/**
 * Flush the queue when the session changes or the hook unmounts, and
 * best-effort on page close: pagehide fires reliably on mobile and
 * bfcache navigations where beforeunload does not, so both. The page
 * listeners are registered ONCE, with the first render's flush - which
 * reads the session through a ref.
 */
function useFlushOnLeave(sessionId: string | null, flush: () => void, cancelAndFlush: () => void) {
  useEffect(() => {
    return () => cancelAndFlush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    const events = ['beforeunload', 'pagehide'] as const;
    events.forEach((e) => window.addEventListener(e, flush));
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      events.forEach((e) => window.removeEventListener(e, flush));
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/** The write queue's refs and collaborators, as one render sees them. */
interface NarrativeQueue {
  queueRef: MutableRefObject<NarrativeEntryDoc[]>;
  timerRef: MutableRefObject<Timer | null>;
  sessionIdRef: MutableRefObject<string | null>;
  mp: typeof mpModule;
  onSyncError?: (message: string) => void;
}

/** Send everything queued, in one batch, to the session the ref names now. */
function flushQueue(q: NarrativeQueue): void {
  const sid = q.sessionIdRef.current;
  if (!sid || q.queueRef.current.length === 0) return;
  q.mp.batchAddNarrativeEntries(sid, q.queueRef.current.splice(0)).catch((err) => {
    console.error(err);
    q.onSyncError?.('Combat log sync failed - entries may be missing for other players.');
  });
}

/** Queue a document; the queue goes out 300ms after the last one. */
function enqueue(q: NarrativeQueue, doc: NarrativeEntryDoc): void {
  q.queueRef.current.push(doc);
  if (q.timerRef.current) clearTimeout(q.timerRef.current);
  q.timerRef.current = setTimeout(() => flushQueue(q), NARRATIVE_FLUSH_DEBOUNCE_MS);
}

/** The session this client writes to, as a ref - so a cleanup flushes to the session it was queued for. */
function useSessionIdRef(sessionId: string | null) {
  const sessionIdRef = useRef(sessionId);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);
  return sessionIdRef;
}

/** The debounced write queue, flushed on session change, unmount and page close. */
function useNarrativeQueue({ sessionId, user, mp, onSyncError, veil }: UseNarrativeSyncParams) {
  const narrativeQueueRef = useRef<NarrativeEntryDoc[]>([]);
  const narrativeFlushTimerRef = useRef<Timer | null>(null);
  const lastNarrativeTsRef = useRef(0);
  const sessionIdRef = useSessionIdRef(sessionId);
  const q = { queueRef: narrativeQueueRef, timerRef: narrativeFlushTimerRef, sessionIdRef, mp, onSyncError };
  const flushNarrativeQueue = () => flushQueue(q);
  const syncNarrativeToFirestore = (entry: NarrativeInput) => {
    if (!sessionId) return;
    try {
      const doc = narrativeDocFrom(entry, nextTimestamp(lastNarrativeTsRef), userNameOf(user));
      enqueue(q, (veil?.current ?? NO_VEIL).apply(doc));
    } catch (e) {
      console.error('Narrative sync error:', e);
    }
  };
  const cancelAndFlush = () => {
    if (narrativeFlushTimerRef.current) clearTimeout(narrativeFlushTimerRef.current);
    flushQueue(q);
  };
  useFlushOnLeave(sessionId, flushNarrativeQueue, cancelAndFlush);
  return { syncNarrativeToFirestore, flushNarrativeQueue, narrativeQueueRef, narrativeFlushTimerRef };
}

/** The shared combat log: a debounced write queue and the live listener. */
export default function useNarrativeSync(params: UseNarrativeSyncParams): UseNarrativeSyncReturn {
  const queue = useNarrativeQueue(params);
  const firestoreNarrative = useNarrativeListener(params.sessionId, params.mp);
  const [narrativeClearTime, setNarrativeClearTime] = useState(0);
  return {
    ...queue,
    firestoreNarrative: narrativeClearTime
      ? firestoreNarrative.filter((e) => toMs(e.timestamp) > narrativeClearTime)
      : firestoreNarrative,
    narrativeClearTime,
    setNarrativeClearTime,
  };
}
